"use client";

import { Suspense, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Auth_Nav_Modal } from "@/components/auth/auth-nav-modal";
import { Api_Paths } from "@/lib/api/paths";
import {
  Auth_Event_Prompt,
  Auth_Success_Notify,
  type Auth_Modal_Mode,
  type Auth_Prompt_Detail,
} from "@/lib/auth/client/events";
import {
  Auth_Access_Clear,
  Auth_Access_Get,
  Auth_Fetch,
  Auth_Session_Restore,
} from "@/lib/auth/client/session";
import { Profile_Read_Display_Name, Profile_Read_Initials } from "@/lib/auth/profile";
import type { App_User } from "@/lib/auth/types";

const Btn_Account =
  "inline-flex items-center gap-1.5 rounded-lg border border-pq-border-strong bg-pq-bg-elevated px-3 py-2 text-xs font-semibold text-pq-fg transition hover:border-amber-500 hover:bg-pq-bg-soft sm:gap-2 sm:px-4 sm:text-sm";

const Btn_Profile =
  "inline-flex items-center gap-2 rounded-lg border border-pq-border-strong bg-pq-bg-elevated px-2 py-1.5 text-xs font-semibold text-pq-fg transition hover:border-amber-500 hover:bg-pq-bg-soft sm:px-3 sm:text-sm";
function Icon_User({ ClassName }: { ClassName?: string }) {
  return (
    <svg
      className={ClassName ?? "h-4 w-4"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      suppressHydrationWarning
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-4 3.5-6 7-6s7 2 7 6" />
    </svg>
  );
}

type Me_Response = {
  user?: App_User;
};

function Auth_Nav_Actions_Inner() {
  const Router = useRouter();
  const Search_Params = useSearchParams();

  const [User, User_Set] = useState<App_User | null>(null);
  const [Profile_Menu_Open, Profile_Menu_Set] = useState(false);
  const [Auth_Modal_Open, Auth_Modal_Set] = useState(false);
  const [Auth_Modal_Mode, Auth_Modal_Mode_Set] = useState<Auth_Modal_Mode>("login");
  const [Next_Path, Next_Path_Set] = useState("/dashboard");

  const Auth_Load_User = useCallback(async () => {
    try {
      if (!Auth_Access_Get()) {
        await Auth_Session_Restore();
      }
      const Response = await Auth_Fetch(Api_Paths.V1.User_Me);
      if (!Response.ok) {
        User_Set(null);
        return;
      }
      const Body = (await Response.json()) as Me_Response;
      User_Set(Body.user ?? null);
    } catch {
      User_Set(null);
    }
  }, []);

  useEffect(() => {
    void Auth_Load_User();
  }, [Auth_Load_User]);

  useEffect(() => {
    const Auth_Param = Search_Params.get("auth");
    if (Auth_Param !== "login" && Auth_Param !== "signup") {
      return;
    }

    Auth_Modal_Mode_Set(Auth_Param);
    Auth_Modal_Set(true);
    Next_Path_Set(Search_Params.get("next") ?? "/dashboard");

    const Clean_Url = new URL(window.location.href);
    Clean_Url.searchParams.delete("auth");
    Clean_Url.searchParams.delete("next");
    const Clean_Path = `${Clean_Url.pathname}${Clean_Url.search}${Clean_Url.hash}`;
    Router.replace(Clean_Path, { scroll: false });
  }, [Router, Search_Params]);

  useEffect(() => {
    function Auth_Prompt_Handle(Event: Event) {
      const Detail = (Event as CustomEvent<Auth_Prompt_Detail>).detail ?? {};
      Profile_Menu_Set(false);
      Auth_Modal_Mode_Set(Detail.Mode === "signup" ? "signup" : "login");
      Next_Path_Set(Detail.Next_Path?.trim() || window.location.pathname || "/");
      Auth_Modal_Set(true);
    }

    window.addEventListener(Auth_Event_Prompt, Auth_Prompt_Handle);
    return () => window.removeEventListener(Auth_Event_Prompt, Auth_Prompt_Handle);
  }, []);

  function Auth_Modal_Open_Handle(Mode: Auth_Modal_Mode) {
    Profile_Menu_Set(false);
    Auth_Modal_Mode_Set(Mode);
    Next_Path_Set("/dashboard");
    Auth_Modal_Set(true);
  }

  function Auth_Modal_Close_Handle() {
    Auth_Modal_Set(false);
  }

  async function Auth_Handle_Signout() {
    Profile_Menu_Set(false);
    await fetch(Api_Paths.V1.Auth.Signout, { method: "POST", credentials: "include" });
    Auth_Access_Clear();
    User_Set(null);
    Router.push("/");
    Router.refresh();
  }

  function Auth_Handle_Success() {
    Auth_Modal_Set(false);
    void Auth_Load_User();
    Auth_Success_Notify();
  }

  if (User) {
    const Initial = Profile_Read_Initials(User);
    const Display_Name = Profile_Read_Display_Name(User);

    return (
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={() => Profile_Menu_Set((Open) => !Open)}
          className={Btn_Profile}
          aria-expanded={Profile_Menu_Open}
          aria-haspopup="menu"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-300 dark:bg-amber-500/20 dark:text-amber-100 dark:ring-amber-400/40">
            {Initial}
          </span>
          <span className="hidden max-w-[120px] truncate sm:inline">{Display_Name}</span>
          <Icon_User ClassName="h-4 w-4 text-pq-fg-subtle sm:hidden" />
        </button>

        {Profile_Menu_Open ? (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 cursor-default"
              aria-label="Close menu"
              onClick={() => Profile_Menu_Set(false)}
            />
            <div
              className="absolute right-0 z-50 mt-2 min-w-[180px] overflow-hidden rounded-xl border border-pq-border bg-pq-bg-elevated p-1.5 shadow-xl shadow-amber-950/10"
              role="menu"
            >
              <p className="truncate px-3 py-2 text-xs text-pq-fg-subtle">{User.email}</p>
              <Link
                href="/dashboard"
                role="menuitem"
                onClick={() => Profile_Menu_Set(false)}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm text-pq-fg transition hover:bg-pq-bg-soft"
              >
                Dashboard
              </Link>
              <Link
                href="/settings"
                role="menuitem"
                onClick={() => Profile_Menu_Set(false)}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm text-pq-fg transition hover:bg-pq-bg-soft"
              >
                Settings
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={Auth_Handle_Signout}
                className="w-full rounded-lg px-3 py-2 text-left text-sm text-pq-fg transition hover:bg-pq-bg-soft"
              >
                Sign out
              </button>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => Auth_Modal_Open_Handle("login")}
        className={`${Btn_Account} ${Auth_Modal_Open ? "border-amber-500 bg-pq-bg-soft" : ""}`}
        aria-expanded={Auth_Modal_Open}
        aria-haspopup="dialog"
      >
        <Icon_User ClassName="h-4 w-4 text-amber-800" />
        <span>Account</span>
      </button>

      <Auth_Nav_Modal
        Open={Auth_Modal_Open}
        Mode={Auth_Modal_Mode}
        Next_Path={Next_Path}
        On_Close={Auth_Modal_Close_Handle}
        On_Mode_Change={Auth_Modal_Mode_Set}
        On_Success={Auth_Handle_Success}
      />
    </div>
  );
}

export function Auth_Nav_Actions() {
  return (
    <Suspense fallback={<div className={`${Btn_Account} shrink-0 opacity-60`}>Account</div>}>
      <Auth_Nav_Actions_Inner />
    </Suspense>
  );
}
